import { DiscountAppliesTo, ThresholdType, TotalItemConditionType, RewardApplyTo } from '../../models';

import { defaultFilters } from './requirements';

import type { Requirement } from './requirements';
import type { CalculationMethods, ManualDefaultApplyTo } from '../../models';
import type { FilterResource, FilterResourceType } from '../shared/filter';
import type { FilterType } from 'src/app/components/lib/wizard/filters';

export enum RewardsStepActions {
  INITIALIZE = 'initialize',
  RESET = 'reset',
  SET_ACTIVE_FILTER = 'set-active-filter',
  SET_ALLOW_NON_CANNABIS_ITEMS = 'set-allow-non-cannabis-items',
  SET_ALLOW_STACKING = 'set-allow-stacking',
  SET_AMOUNT_OFF = 'set-amount-off',
  SET_DISCOUNT_APPLIES_TO = 'set-discount-applies-to',
  SET_DISCOUNT_METHOD = 'set-discount-method',
  SET_IGNORE_NET_TAXES = 'set-ignore-net-taxes',
  SET_LOYALTY_MULTIPLIER = 'set-loyalty-multiplier',
  SET_MANUAL_APPLY_TO_METHOD = 'set-manual-apply-to-method',
  SET_PERCENT_OFF = 'set-percent-off',
  SET_REQUIRE_SAME_PRODUCT = 'set-require-same-product',
  SET_REWARD_APPLY_TO = 'set-reward-apply-to',
  SET_REWARD_CONDITION = 'set-reward-condition',
  SET_REWARD_MAXIMUM_ITEMS = 'set-reward-maximum-items',
  SET_REWARD_MINIMUM_GRAMS = 'set-reward-minimum-grams',
  SET_REWARD_MINIMUM_ITEMS = 'set-reward-minimum-items',
  SET_REWARD_MINIMUM_SPEND = 'set-reward-minimum-spend',
  SET_REWARD_PRODUCTS_FILTER = 'set-reward-products-filter',
  SET_REWARD_TOTAL_ITEM_CONDITION = 'set-reward-total-condition',
  VISITED = 'visited',
}

type InitializeAction = {
  payload: RewardsStepState;
  type: RewardsStepActions.INITIALIZE;
};

type ResetAction = {
  type: RewardsStepActions.RESET;
};

type SetActiveFilterAction = {
  payload: FilterResourceType | undefined;
  type: RewardsStepActions.SET_ACTIVE_FILTER;
};

type SetAllowNonCannabisItems = {
  payload: boolean;
  type: RewardsStepActions.SET_ALLOW_NON_CANNABIS_ITEMS;
};

type SetAllowStackingAction = {
  payload: boolean;
  type: RewardsStepActions.SET_ALLOW_STACKING;
};

type SetAmountOffAction = {
  payload: number;
  type: RewardsStepActions.SET_AMOUNT_OFF;
};

type SetDiscountAppliesToAction = {
  payload: DiscountAppliesTo;
  type: RewardsStepActions.SET_DISCOUNT_APPLIES_TO;
};

type SetDiscountMethodAction = {
  payload: CalculationMethods;
  type: RewardsStepActions.SET_DISCOUNT_METHOD;
};

type SetIgnoreNetTaxesAction = {
  payload: boolean;
  type: RewardsStepActions.SET_IGNORE_NET_TAXES;
};

type SetLoyaltyMultiplierAction = {
  payload: number | '';
  type: RewardsStepActions.SET_LOYALTY_MULTIPLIER;
};

type SetManualApplyToMethod = {
  payload: ManualDefaultApplyTo;
  type: RewardsStepActions.SET_MANUAL_APPLY_TO_METHOD;
};

type SetPercentOffAction = {
  payload: number;
  type: RewardsStepActions.SET_PERCENT_OFF;
};

type SetRequireSameProductAction = {
  payload: boolean;
  type: RewardsStepActions.SET_REQUIRE_SAME_PRODUCT;
};

type SetRewardApplyToAction = {
  payload: RewardApplyTo;
  type: RewardsStepActions.SET_REWARD_APPLY_TO;
};

type SetRewardConditionAction = {
  payload: ThresholdType;
  type: RewardsStepActions.SET_REWARD_CONDITION;
};

type SetRewardMaximumItemsAction = {
  payload: number | '';
  type: RewardsStepActions.SET_REWARD_MAXIMUM_ITEMS;
};

type SetRewardMinimumGramsAction = {
  payload: number | '';
  type: RewardsStepActions.SET_REWARD_MINIMUM_GRAMS;
};

type SetRewardMinimumItemsAction = {
  payload: number | '';
  type: RewardsStepActions.SET_REWARD_MINIMUM_ITEMS;
};

type SetRewardMinimumSpendAction = {
  payload: number | '';
  type: RewardsStepActions.SET_REWARD_MINIMUM_SPEND;
};

type SetRewardProductsFilterAction = {
  payload: {
    resources: FilterResource[];
    resourceType: FilterResourceType;
    selectionType: FilterType;
  };
  type: RewardsStepActions.SET_REWARD_PRODUCTS_FILTER;
};

type SetRewardTotalItemConditionAction = {
  payload: TotalItemConditionType;
  type: RewardsStepActions.SET_REWARD_TOTAL_ITEM_CONDITION;
};

type SetVisitedAction = {
  type: RewardsStepActions.VISITED;
};

export type RewardsStepAction =
  | InitializeAction
  | ResetAction
  | SetActiveFilterAction
  | SetAllowNonCannabisItems
  | SetAllowStackingAction
  | SetAmountOffAction
  | SetDiscountAppliesToAction
  | SetDiscountMethodAction
  | SetIgnoreNetTaxesAction
  | SetLoyaltyMultiplierAction
  | SetManualApplyToMethod
  | SetPercentOffAction
  | SetRequireSameProductAction
  | SetRewardApplyToAction
  | SetRewardConditionAction
  | SetRewardMaximumItemsAction
  | SetRewardMinimumGramsAction
  | SetRewardMinimumItemsAction
  | SetRewardMinimumSpendAction
  | SetRewardProductsFilterAction
  | SetRewardTotalItemConditionAction
  | SetVisitedAction;

export type RewardCondition = Requirement & {
  applyTo: RewardApplyTo;
};

export type RewardsStepState = {
  activeFilter: FilterResourceType | undefined;
  allowStacking: boolean;
  amountOff: number | '';
  condition: RewardCondition;
  discountAppliesTo: DiscountAppliesTo;
  discountMethod: CalculationMethods | 0;
  ignoreNetTaxes: boolean;
  loyaltyMultiplier: number | '';
  manualApplyToMethod: ManualDefaultApplyTo | 0;
  percentOff: number | '';
  visited: boolean;
};

export const defaultRewardConditionState: RewardCondition = {
  applyTo: RewardApplyTo.all,
  condition: ThresholdType.NoThreshold,
  filters: { ...defaultFilters },
  maximumItems: '',
  minimumGrams: '',
  minimumItems: '',
  minimumSpend: '',
  allowNonCannabisItems: true,
  requireSameProduct: false,
  totalItemCondition: TotalItemConditionType.AtLeast,
  id: null,
};

export const rewardsStepDefaultState: RewardsStepState = {
  activeFilter: undefined,
  allowStacking: false,
  amountOff: '',
  condition: { ...defaultRewardConditionState },
  discountAppliesTo: DiscountAppliesTo.SameProducts,
  discountMethod: 0,
  ignoreNetTaxes: false,
  manualApplyToMethod: 1,
  percentOff: '',
  visited: false,
  loyaltyMultiplier: '',
};

export function rewardsStepReducer(state: RewardsStepState, action: RewardsStepAction) {
  switch (action.type) {
    case RewardsStepActions.INITIALIZE:
      return {
        ...action.payload,
      };
    case RewardsStepActions.RESET:
      return {
        ...rewardsStepDefaultState,
      };
    case RewardsStepActions.SET_ACTIVE_FILTER:
      return {
        ...state,
        activeFilter: action.payload,
      };
    case RewardsStepActions.SET_ALLOW_NON_CANNABIS_ITEMS:
      return {
        ...state,
        condition: {
          ...state.condition,
          allowNonCannabisItems: action.payload,
        },
      };
    case RewardsStepActions.SET_ALLOW_STACKING:
      return {
        ...state,
        allowStacking: action.payload,
      };
    case RewardsStepActions.SET_AMOUNT_OFF:
      return {
        ...state,
        amountOff: action.payload,
      };
    case RewardsStepActions.SET_IGNORE_NET_TAXES:
      return {
        ...state,
        ignoreNetTaxes: action.payload,
      };
    case RewardsStepActions.SET_DISCOUNT_APPLIES_TO:
      return {
        ...state,
        discountAppliesTo: action.payload,
      };
    case RewardsStepActions.SET_DISCOUNT_METHOD:
      return {
        ...state,
        discountMethod: action.payload,
      };
    case RewardsStepActions.SET_LOYALTY_MULTIPLIER:
      return {
        ...state,
        loyaltyMultiplier: action.payload,
      };
    case RewardsStepActions.SET_MANUAL_APPLY_TO_METHOD:
      return {
        ...state,
        manualApplyToMethod: action.payload,
      };
    case RewardsStepActions.SET_PERCENT_OFF:
      return {
        ...state,
        percentOff: action.payload,
      };
    case RewardsStepActions.SET_REQUIRE_SAME_PRODUCT:
      return {
        ...state,
        condition: {
          ...state.condition,
          requireSameProduct: action.payload,
        },
      };
    case RewardsStepActions.SET_REWARD_APPLY_TO:
      return {
        ...state,
        condition: { ...state.condition, applyTo: action.payload },
      };
    case RewardsStepActions.SET_REWARD_CONDITION:
      return {
        ...state,
        condition: {
          ...state.condition,
          condition: action.payload,
        },
      };
    case RewardsStepActions.SET_REWARD_MAXIMUM_ITEMS:
      return {
        ...state,
        condition: {
          ...state.condition,
          maximumItems: action.payload,
        },
      };
    case RewardsStepActions.SET_REWARD_MINIMUM_GRAMS:
      return {
        ...state,
        condition: {
          ...state.condition,
          minimumGrams: action.payload,
        },
      };
    case RewardsStepActions.SET_REWARD_MINIMUM_ITEMS:
      return {
        ...state,
        condition: {
          ...state.condition,
          minimumItems: action.payload,
        },
      };
    case RewardsStepActions.SET_REWARD_MINIMUM_SPEND:
      return {
        ...state,
        condition: {
          ...state.condition,
          minimumSpend: action.payload,
        },
      };
    case RewardsStepActions.SET_REWARD_PRODUCTS_FILTER:
      return {
        ...state,
        activeFilter: undefined,
        condition: {
          ...state.condition,
          filters: {
            ...state.condition.filters,
            [action.payload.resourceType]: {
              resources: action.payload.resources,
              selectionType: action.payload.selectionType,
            },
          },
        },
      };
    case RewardsStepActions.SET_REWARD_TOTAL_ITEM_CONDITION:
      return {
        ...state,
        condition: {
          ...state.condition,
          totalItemCondition: action.payload,
        },
      };
    case RewardsStepActions.VISITED:
      return {
        ...state,
        visited: true,
      };
    default:
      return state;
  }
}
